import { useState } from 'react'

import { Box, Typography, FormControl, Select, MenuItem, LinearProgress } from '@mui/material'
import { LineChart } from '@mui/x-charts/LineChart'

import { useGetProxyTrafficChartsQuery } from 'services/dashboard-analytics/api'

const ProxyTrafficChart = () => {
  const { data: proxyData } = useGetProxyTrafficChartsQuery()
  const data = proxyData
  const [selectedProvider, setSelectedProvider] = useState('All')
  const providers = ['All', ...(data?.map((p) => p.provider) || [])]

  const getChartData = () => {
    if (selectedProvider === 'All') {
      const allDates = new Set<string>()
      const allUsageData: Record<string, number> = {}

      data?.forEach((proxy) => {
        proxy.dailyUsage.dates.forEach((date: string, index: number) => {
          allDates.add(date)
          allUsageData[date] = (allUsageData[date] || 0) + proxy.dailyUsage.usageGb[index]
        })
      })

      const sortedDates = Array.from(allDates).sort()
      const bandwidthData = sortedDates.map((date) => allUsageData[date] || 0)

      return { dates: sortedDates, bandwidthData }
    } else {
      const proxy = data?.find((p) => p.provider === selectedProvider)
      if (proxy) {
        return {
          dates: proxy.dailyUsage.dates,
          bandwidthData: proxy.dailyUsage.usageGb,
        }
      }
      return { dates: [], bandwidthData: [] }
    }
  }

  const { dates, bandwidthData } = getChartData()
  return (
    <Box
      sx={{
        height: '100%',
        p: '16px',
        py: '8px',
        boxShadow: '0px 2px 10px 2px rgba(0, 0, 0, 0.1)',
        borderRadius: '4px',
        border: '1px solid rgba(0, 0, 0, 0.12)',
      }}
    >
      <Box sx={{ display: 'flex', gap: '24px', height: '100%' }}>
        <Box sx={{ flex: '75%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', mb: '16px' }}>
            <Typography variant="h3">Traffic </Typography>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select
                value={selectedProvider}
                onChange={(e) => setSelectedProvider(e.target.value)}
                displayEmpty
                sx={{
                  height: '25px',
                }}
              >
                {providers.map((provider) => (
                  <MenuItem key={provider} value={provider}>
                    {provider.charAt(0).toUpperCase() + provider.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0 }}>
            <Typography variant="h6">Bandwidth (GB)</Typography>
            <LineChart
              xAxis={[
                {
                  data: dates,
                  scaleType: 'band',
                  valueFormatter: (value) => value,
                },
              ]}
              series={[
                {
                  data: bandwidthData,
                  label: 'Bandwidth Usage',
                  color: '#9c27b0',
                  curve: 'linear',
                  showMark: true,
                  area: true,
                },
              ]}
              height={350}
              slotProps={{
                legend: {
                  hidden: true,
                },
              }}
              tooltip={{
                trigger: 'axis',
              }}
            />
          </Box>
        </Box>

        <Box sx={{ flex: '25%', display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" sx={{ mb: '16px', fontWeight: 600 }}>
            Traffic Left
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {data?.map((proxy) => (
              <Box
                key={proxy.provider}
                sx={{ p: 2, border: '1px solid rgba(0, 0, 0, 0.12)', borderRadius: 1 }}
              >
                <Typography variant="subtitle2" sx={{ mb: '8px', fontWeight: 600 }}>
                  {proxy.provider.charAt(0).toUpperCase() + proxy.provider.slice(1)}
                </Typography>

                <Typography variant="body2" sx={{ mb: '8px', color: 'text.secondary' }}>
                  {proxy.leftGb}GB left
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: '8px' }}>
                  <LinearProgress
                    variant="determinate"
                    value={proxy.usagePercent}
                    sx={{
                      flex: 1,
                      height: '8px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(0, 0, 0, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#ff9800',
                        borderRadius: '8px',
                      },
                    }}
                  />
                </Box>

                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {proxy.usagePercent}% used
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default ProxyTrafficChart
